import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Label } from "@/components/ui/label";

import { canAccess, type Actor } from "../providers/commons/canAccess";
import { getProjectScope } from "../providers/commons/canAccessCarousel";
import type { CrmDataProvider } from "../providers/types";
import type {
  AppRole,
  BusinessArm,
  Project,
  SalesProject,
  QuoterConfig,
  QuoterConfigResource,
  QuoterModel,
  QuoterModelListPrice,
  QuoterPriceList,
  QuoterScheme,
} from "../types";
import type { QuoterProjectData } from "./quoterEngine";

/**
 * Almikia Epic EQ2 (TASK-099): data access shared by the Direccion quoter
 * screens (config, price lists, models and schemes, verification, panel).
 */

export const QUOTER_ADMIN_QUERY_KEY = "quoter_admin";

/** Large enough for every per-project collection (one project has tens of rows). */
const PAGE = { page: 1, perPage: 1000 };

/**
 * Projects that already have a quoter configuration, by name. With `editableOnly`
 * (the configuration screen, Almikia batch EQ3 TASK-113) only the projects whose
 * plans the identity may edit: every project for direccion and the BPO
 * lead_manager, the led projects for a lider, none without a role.
 */
export const useQuoterProjects = (editableOnly = false) => {
  const dataProvider = useDataProvider();
  const { identity, isPending: isIdentityPending } = useGetIdentity();
  return useQuery({
    queryKey: [
      QUOTER_ADMIN_QUERY_KEY,
      "projects",
      editableOnly ? (identity?.id ?? null) : "all",
    ],
    enabled: !editableOnly || !isIdentityPending,
    queryFn: async (): Promise<Project[]> => {
      const role = identity?.role as AppRole | undefined;
      if (editableOnly && !role) return [];
      const [configs, projects] = await Promise.all([
        dataProvider.getList<QuoterConfig>("quoter_configs", {
          pagination: PAGE,
          sort: { field: "id", order: "ASC" },
          filter: {},
        }),
        dataProvider.getList<Project>("projects", {
          pagination: PAGE,
          sort: { field: "name", order: "ASC" },
          filter: {},
        }),
      ]);
      const configured = new Set(configs.data.map((row) => row.project_id));
      const withConfig = projects.data.filter((project) =>
        configured.has(project.id),
      );
      if (!editableOnly || !role) return withConfig;
      // Her live project scope (only a lider needs it; RLS limits what she reads).
      const memberships =
        role === "lider"
          ? (
              await dataProvider.getList<SalesProject>("sales_projects", {
                pagination: PAGE,
                sort: { field: "id", order: "ASC" },
                filter: {},
              })
            ).data
          : [];
      const actor: Actor = {
        role,
        id: identity?.id,
        business_arm: (identity?.business_arm as BusinessArm | null) ?? null,
        ...getProjectScope(identity?.id, memberships),
      };
      return withConfig.filter((project) =>
        canAccess(actor, {
          action: "edit",
          resource: "quoter_configs",
          record: { project_id: project.id },
        }),
      );
    },
  });
};

/** Config, models, price lists, captured prices and schemes of ONE project. */
export const useQuoterProjectData = (projectId: Identifier | undefined) => {
  const dataProvider = useDataProvider();
  return useQuery({
    queryKey: [QUOTER_ADMIN_QUERY_KEY, "project", projectId],
    enabled: projectId != null,
    queryFn: async (): Promise<QuoterProjectData> => {
      const list = async <RecordType extends { id: Identifier }>(
        resource: QuoterConfigResource,
        filter: Record<string, unknown>,
        field: string,
      ) =>
        (
          await dataProvider.getList<RecordType>(resource, {
            pagination: PAGE,
            sort: { field, order: "ASC" },
            filter,
          })
        ).data;
      const byProject = { project_id: projectId };
      const [configs, models, priceLists, schemes, allListPrices] =
        await Promise.all([
          list<QuoterConfig>("quoter_configs", byProject, "id"),
          list<QuoterModel>("quoter_models", byProject, "sort_order"),
          list<QuoterPriceList>("quoter_price_lists", byProject, "list_number"),
          list<QuoterScheme>("quoter_schemes", byProject, "sort_order"),
          list<QuoterModelListPrice>("quoter_model_list_prices", {}, "id"),
        ]);
      const listIds = new Set(priceLists.map((row) => row.id));
      return {
        config: configs[0],
        models,
        priceLists,
        schemes,
        listPrices: allListPrices.filter((row) =>
          listIds.has(row.price_list_id),
        ),
      };
    },
  });
};

/** Refreshes every quoter admin query after a write. */
export const useRefreshQuoterAdmin = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [QUOTER_ADMIN_QUERY_KEY] });
};

/** Saves one config row through the provider (insert or update, never delete). */
export const useSaveQuoterConfig = () => {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const refresh = useRefreshQuoterAdmin();
  return async (
    resource: QuoterConfigResource,
    id: Identifier | null,
    data: Record<string, unknown>,
  ) => {
    const saved = await dataProvider.saveQuoterConfig(resource, id, data);
    await refresh();
    return saved;
  };
};

/** Selected project, defaulting to the first configured one (`editableOnly`: see above). */
export const useSelectedQuoterProject = (editableOnly = false) => {
  const projects = useQuoterProjects(editableOnly);
  const [chosen, setChosen] = useState<Identifier | undefined>(undefined);
  const list = projects.data ?? [];
  const projectId =
    chosen != null && list.some((project) => project.id === chosen)
      ? chosen
      : list[0]?.id;
  return { projects, projectId, setProjectId: setChosen };
};

/** The one-question-per-screen project picker of the quoter admin screens. */
export const QuoterProjectPicker = ({
  projects,
  value,
  onChange,
}: {
  projects: Project[];
  value: Identifier | undefined;
  onChange: (projectId: Identifier) => void;
}) => {
  const translate = useTranslate();
  return (
    <div className="space-y-1 px-1">
      <Label htmlFor="quoter-project-picker">
        {translate("crm.quoter.config.project")}
      </Label>
      <select
        id="quoter-project-picker"
        className="h-11 w-full rounded-md border bg-background px-3 text-base sm:h-9 sm:max-w-xs sm:text-sm"
        value={value == null ? "" : String(value)}
        onChange={(event) => {
          const picked = projects.find(
            (project) => String(project.id) === event.target.value,
          );
          if (picked) onChange(picked.id);
        }}
      >
        {projects.map((project) => (
          <option key={project.id} value={String(project.id)}>
            {project.name}
          </option>
        ))}
      </select>
    </div>
  );
};
